"use client";
import { IBaseLabelValue } from "@/interfaces";
import { CustomButton, FlexBetween, FlexCenter, FlexCol } from "@/components";
import { cn, formatJalaliDateWithSlash, formatNumberWithDots } from "@/lib";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import InputDataContent from "@/components/inquiryDetails/inputDataContent";
import { routes } from "@/routes";
import DownloadPdf from "../../pdfTemplates/downloadPdf";
import React, { useRef } from "react";
import ResultContent from "@/components/resultContent";
import PdfTemplate from "@/components/pdfTemplates/pdfTemplate";
import { IRawPurchasedRegisterCheckInquiryResData } from "@/interfaces/inquiries/chequeInquiry";

const RegisterChequeInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedRegisterCheckInquiryResData;
}) => {
  const t = useTranslations("serviceResponseDetails");
  const router = useRouter();
  const chequeData = data.data.data;
  const chequeInput = data.data.input;

  const inputContent: IBaseLabelValue[] = [
    {
      label: t("chequeInquiry.nationalCode"),
      value: chequeInput.national_code,
    },
    {
      label: t("chequeInquiry.sayadId"),
      value: chequeInput.sayad_id,
    },
  ];
  const pdfContentRef = useRef<HTMLDivElement>(null);
  const chequeAmount = `${formatNumberWithDots(chequeData.amount)} ${chequeData.currency === 1 ? "ریال" : "تومان"}`;
  const resultContent: IBaseLabelValue[] = [
    {
      label: t("chequeInquiry.serialNo"),
      value: chequeData.serialNo || "-",
    },
    {
      label: t("chequeInquiry.seriesNo"),
      value: chequeData.seriesNo || "-",
    },
    {
      label: t("chequeInquiry.amount"),
      value: chequeAmount || "-",
    },
    {
      label: t("chequeInquiry.dueDate"),
      value: formatJalaliDateWithSlash(chequeData.dueDate) || "-",
    },
    {
      label: t("chequeInquiry.description"),
      value: chequeData.description || "-",
    },
    {
      label: t("chequeInquiry.fromIban"),
      value: chequeData.fromIban || "-",
    },
    {
      label: t("chequeInquiry.branchCode"),
      value: chequeData.branchCode || "-",
    },
    {
      label: t("chequeInquiry.bankCode"),
      value: chequeData.bankCode || "-",
    },
    {
      label: t("chequeInquiry.chequeTypeDescription"),
      value: chequeData.chequeTypeDescription || "-",
    },
    {
      label: t("chequeInquiry.chequeMediaDescription"),
      value: chequeData.chequeMediaDescription || "-",
    },
    {
      label: t("chequeInquiry.chequeStatusDescription"),
      value: chequeData.chequeStatusDescription || "-",
    },
    {
      label: t("chequeInquiry.reasonDescription"),
      value: chequeData.reasonDescription || "-",
    },
    {
      label: t("chequeInquiry.guaranteeStatusDescription"),
      value: chequeData.guaranteeStatusDescription || "-",
    },
    {
      label: t("chequeInquiry.blockStatusDescription"),
      value: chequeData.blockStatusDescription || "-",
    },
    {
      label: t("chequeInquiry.lockedDescription"),
      value: chequeData.lockedDescription || "-",
    },
    {
      label: t("chequeInquiry.lockerBankCode"),
      value: chequeData.lockerBankCode || "-",
    },
    {
      label: t("chequeInquiry.lockerBranchCode"),
      value: chequeData.lockerBranchCode || "-",
    },
  ];

  const HOLDER_MAP = {
    name: "نام دارنده چک",
    idCode: "کد شناسایی دارنده چک",
    idTypes: "نوع شخص",
  };

  const holders = chequeData.holders.flatMap((item): IBaseLabelValue[] => {
    return Object.keys(item).map((key) => ({
      label: HOLDER_MAP[key as keyof typeof HOLDER_MAP],
      value:
        key === "idTypes"
          ? item[key] === "realCustomer"
            ? "حقیقی"
            : "حقوقی"
          : (String(item[key as keyof typeof item]) ?? "-"),
    }));
  });

  const SIGNER_MAP = {
    name: "نام امضاء کننده",
    hasLegalStamp: "امضاء کننده وارد شده به منزله مهر حقوقی می باشد",
  };
  const signers = chequeData.signers.flatMap((item): IBaseLabelValue[] => {
    return Object.keys(item).map((key) => ({
      label: SIGNER_MAP[key as keyof typeof SIGNER_MAP],
      value:
        key === "hasLegalStamp"
          ? item[key]
            ? "بله"
            : "خیر"
          : (String(item[key as keyof typeof item]) ?? "-"),
    }));
  });
  return (
    <div className="shadow-main from-emerald-mint relative basis-[500px] rounded-2xl bg-linear-to-b to-transparent p-[2px] lg:basis-[640px]">
      <FlexCol className="items-stretch rounded-2xl bg-white p-8">
        <h2 className="mb-5 text-center font-bold lg:text-lg">
          استعلام ثبت چک صیادی
        </h2>
        <InputDataContent
          content={inputContent}
          className="le p-6 md:p-8"
          itemClassName="flex-col md:flex-row leading-9 md:leading-none"
        />
        <FlexBetween className="items-center md:mb-3">
          <h2 className="my-5 text-center text-lg font-bold md:my-0">
            {t("accountToIban.resultTitle")}
          </h2>
          <DownloadPdf
            id="bounced-check-inquiry-download-pdf"
            htmlContentRef={pdfContentRef}
            useHtmlToPdf={true}
            fileName={`استعلام-ثبت-چک-صیادی-${chequeInput.sayad_id}`}
            tableTitle="استعلام ثبت چک صیادی"
          />
        </FlexBetween>
        <ResultContent
          title="اطلاعات چک"
          content={resultContent}
          className="md:gap-4"
        />
        <ResultContent
          title="اطلاعات چک"
          content={holders}
          className="md:gap-4"
        />
        <ResultContent
          title="امضاء کنندگان چک"
          content={signers}
          className="md:gap-4"
        />
        <FlexCenter className="flex flex-col justify-center md:flex-row md:gap-6">
          <DownloadPdf
            id="bounced-check-inquiry-download-pdf"
            htmlContentRef={pdfContentRef}
            useHtmlToPdf={true}
            fileName={`استعلام-ثبت-چک-صیادی-${chequeInput.sayad_id}`}
            tableTitle="استعلام ثبت چک صیادی"
            className="order-2 flex cursor-pointer items-center justify-center rounded-md border-none! bg-red-700 px-8 py-[6px] text-white transition-all hover:bg-red-800 md:order-1 lg:rounded-lg lg:py-[8px]"
            showIcon={false}
            labelClass="text-xs lg:text-sm xl:text-base font-medium"
          />
          <div className="order-1 mb-4 flex gap-4 md:order-2 md:m-0 xl:gap-6">
            <CustomButton
              id="details-bounced-check-inquiry-back"
              className={cn("w-fit bg-gray-500")}
              onClick={() =>
                router.push(routes.main.INQUIRY_HISTORY.primaryPath)
              }
            >
              {t("companyInformationInquiry.backButton")}
            </CustomButton>
            <CustomButton
              id="details-bounced-check-inquiry-new-request"
              onClick={() =>
                router.push(routes.services.Cheque_Inquiry.rewritePath)
              }
              className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
            >
              {t("newRequest")}
            </CustomButton>
          </div>
        </FlexCenter>
      </FlexCol>
      <div
        ref={pdfContentRef}
        style={{
          position: "fixed",
          left: "-9999px",
          top: "-9999px",
          width: "800px",
          fontFamily: "IRANYekan, sans-serif",
          margin: "2rem auto",
        }}
      >
        <PdfTemplate
          resultContent={resultContent}
          code={data.code}
          dateTime={data.created_at}
          tableTitle="اطلاعات چک"
        />
        <PdfTemplate
          resultContent={holders}
          code={data.code}
          dateTime={data.created_at}
          tableTitle="دارندگان چک"
        />
        <PdfTemplate
          resultContent={signers}
          code={data.code}
          dateTime={data.created_at}
          tableTitle="امضاء کنندگان چک"
        />
      </div>
    </div>
  );
};

export default RegisterChequeInquiryDetails;
